/**
 * PushNotificationToggle Component
 *
 * A toggle switch for enabling/disabling push notifications
 * in user settings.
 */

"use client";

import { usePushNotifications } from "@/hooks/usePushNotifications";
import type { PushSupportStatus } from "@/lib/fcm";
import { Button, Switch } from "@heroui/react";
import { addToast } from "@heroui/toast";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { PWAInstallGuide } from "./PWAInstallGuide";

interface PushNotificationToggleProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * Get the appropriate message key for each support status
 */
function getUnsupportedMessageKey(status: PushSupportStatus): string {
  switch (status) {
    case "ios-needs-pwa":
      return "settings.pushNotificationsIOSPWA";
    case "insecure-context":
      return "settings.pushNotificationsInsecure";
    case "no-service-worker":
    case "no-push-manager":
    case "no-notification-api":
    default:
      return "settings.pushNotificationsNotSupported";
  }
}

export function PushNotificationToggle({
  className = "",
}: PushNotificationToggleProps) {
  const t = useTranslations();
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const {
    isSupported,
    supportStatus,
    permission,
    isEnabled,
    isLoading,
    enableNotifications,
    disableNotifications,
  } = usePushNotifications();

  // Show platform-specific message if not supported
  if (!isSupported) {
    const isIOSPWAIssue = supportStatus === "ios-needs-pwa";

    return (
      <div className={className}>
        <div className="flex items-center gap-3">
          <Icon
            icon={isIOSPWAIssue ? "solar:iphone-linear" : "solar:bell-off-linear"}
            className={`h-5 w-5 ${isIOSPWAIssue ? "text-warning" : "text-default-400"}`}
            aria-hidden="true"
          />
          <div className="flex-1">
            <p
              className={`text-sm font-medium ${isIOSPWAIssue ? "text-foreground" : "text-default-400"}`}
            >
              {t("settings.pushNotifications")}
            </p>
            <p
              className={`text-xs ${isIOSPWAIssue ? "text-warning" : "text-default-400"}`}
            >
              {t(getUnsupportedMessageKey(supportStatus))}
            </p>
          </div>
          {isIOSPWAIssue && (
            <Button
              size="sm"
              variant="flat"
              color="warning"
              onPress={() => setShowInstallGuide(!showInstallGuide)}
              endContent={
                <Icon
                  icon={showInstallGuide ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"}
                  className="h-4 w-4"
                />
              }
            >
              {showInstallGuide ? t("common.showLess") : t("common.showMore")}
            </Button>
          )}
        </div>
        {isIOSPWAIssue && showInstallGuide && (
          <div className="mt-4">
            <PWAInstallGuide onClose={() => setShowInstallGuide(false)} />
          </div>
        )}
      </div>
    );
  }

  // Permission was denied - show message
  if (permission === "denied") {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <Icon
          icon="solar:bell-off-linear"
          className="h-5 w-5 text-warning"
          aria-hidden="true"
        />
        <div className="flex-1">
          <p className="text-sm font-medium">
            {t("settings.pushNotifications")}
          </p>
          <p className="text-xs text-warning">
            {t("settings.pushNotificationsBlocked")}
          </p>
        </div>
      </div>
    );
  }

  const handleChange = (enabled: boolean) => {
    if (enabled) {
      // iOS Safari requires Notification.requestPermission() to be called
      // synchronously within the user gesture (tap). If we make this async,
      // the permission request happens in a microtask which breaks the
      // user gesture chain and iOS silently blocks the permission.
      if (permission !== "granted" && "Notification" in window) {
        Notification.requestPermission().then((result) => {
          if (result === "granted") {
            enableNotifications();
          } else if (result === "denied") {
            addToast({
              title: t("notifications.permissionDenied"),
              description: t("notifications.permissionDeniedDescription"),
              color: "danger",
              severity: "danger",
            });
          }
        });
      } else {
        enableNotifications();
      }
    } else {
      disableNotifications();
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-3">
        <Icon
          icon={isEnabled ? "solar:bell-bold" : "solar:bell-linear"}
          className={`h-5 w-5 ${isEnabled ? "text-primary" : "text-default-400"}`}
          aria-hidden="true"
        />
        <div className="flex-1">
          <p className="text-sm font-medium">{t("settings.pushNotifications")}</p>
          <p className="text-xs text-default-500">
            {t("settings.pushNotificationsDescription")}
          </p>
        </div>
        {isLoading && (
          <div className="w-4 h-4 border-2 border-default-300 border-t-primary rounded-full animate-spin" />
        )}
        <Switch
          isSelected={isEnabled}
          onValueChange={handleChange}
          isDisabled={isLoading}
          aria-label={t("settings.pushNotifications")}
          size="sm"
        />
      </div>

      {/* Installation guide button */}
      <div className="mt-3 pl-8">
        <Button
          size="sm"
          variant="light"
          className="text-default-500 px-0 h-auto min-w-0"
          onPress={() => setShowInstallGuide(!showInstallGuide)}
          startContent={
            <Icon
              icon="solar:smartphone-2-linear"
              className="h-4 w-4"
            />
          }
          endContent={
            <Icon
              icon={showInstallGuide ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"}
              className="h-3 w-3"
            />
          }
        >
          {t("pwaInstall.showInstallInstructions")}
        </Button>
      </div>

      {showInstallGuide && (
        <div className="mt-4">
          <PWAInstallGuide onClose={() => setShowInstallGuide(false)} />
        </div>
      )}
    </div>
  );
}
